/** V-logo navigation with Canvas Return Context validation
 * (VISTOERA_V_LOGO_STALE_CANVAS_RETURN_CONTEXT_AND_SAFE_FALLBACK_P0_V1,
 * TASK-00056).
 *
 * Pure resolver — API probe is injected by `SaasBacksideBrandLogo` so node
 * tests never pull in `import.meta.env`. */

import {
  clearCanvasNavigationContext,
  contentProjectCanvasRoute,
  contentProjectsListRoute,
  readCanvasNavigationContext,
  resolveVLogoDestination,
  workspaceHomeCanvasRoute,
  type VLogoDestination,
} from './canvasNavigationContext'
import { isOwnCanvasProject, unwrapProjectDetail } from './canvasProjectOwnership'

export type ContentProjectProbe = (workspaceId: string, projectId: string) => Promise<unknown>

function isNotFoundError(err: unknown): boolean {
  const e = err as { status?: number; code?: string }
  if (e?.status === 404) return true
  const code = typeof e?.code === 'string' ? e.code.toUpperCase() : ''
  return code === 'NOT_FOUND' || code === 'CONTENT_PROJECT_NOT_FOUND'
}

export interface ValidatedVLogoOptions {
  /** Signed-in user. Without a confirmed identity ownership cannot be proven. */
  userId?: string | null
  /** Ownership predicate (default: {@link isOwnCanvasProject}). */
  isOwn?: (project: unknown, userId: string) => boolean
}

/** Resolve V-logo destination (VistoeraTASK-00577).
 *
 * The V logo is the signed-in account's PERSONAL Canvas entry. A stored Canvas
 * Return Context is resumed only if the project it names is provably OWNED by this
 * user (`created_by_user_id` / `is_mine` — never mere `can_view` / `can_edit`).
 * Anything else — a teammate's project (e.g. the Workspace Owner can VIEW members'
 * private projects), a deleted / forbidden project, an unverifiable one — is
 * ignored and the click enters `/home`, which resolves the user's own last-opened
 * project and then their own Home Canvas. Real errors are not turned into a blank
 * canvas: `/home` surfaces its own retryable error state. */
export async function resolveValidatedVLogoDestination(
  workspaceId: string,
  pathname: string,
  probe: ContentProjectProbe,
  options: ValidatedVLogoOptions = {},
): Promise<VLogoDestination> {
  const home: VLogoDestination = { kind: 'ENTER_HOME_CANVAS', route: workspaceHomeCanvasRoute(workspaceId) }
  const stored = readCanvasNavigationContext(workspaceId)
  if (!stored?.contentProjectId) {
    return resolveVLogoDestination(workspaceId, pathname)
  }

  const userId = options.userId ?? null
  if (!userId) return home // identity unconfirmed — never resume a stored project blindly

  const isOwn = options.isOwn ?? isOwnCanvasProject
  try {
    const detail = unwrapProjectDetail(await probe(workspaceId, stored.contentProjectId))
    if (isOwn(detail, userId)) {
      return {
        kind: 'RETURN_TO_CANVAS',
        route: contentProjectCanvasRoute(workspaceId, stored.contentProjectId),
      }
    }
    // Viewable but not mine: not a personal-canvas target. Forget it as context.
    clearCanvasNavigationContext(workspaceId)
    return home
  } catch (err) {
    const status = (err as { status?: number })?.status
    if (isNotFoundError(err) || status === 403) {
      clearCanvasNavigationContext(workspaceId)
    }
    // Transient failure: ownership unverifiable → let `/home` resolve (and retry) safely.
    return home
  }
}

/** Safe backoffice landing when Production Canvas cannot load the routed
 * project (stale deep link). One-shot navigate to Content Projects list —
 * not the Logo's `/home` entry (VistoeraTASK-00552 / 00573). */
export function resolveCanvasLoadNotFoundFallback(
  workspaceId: string,
  _pathname?: string,
): { route: string } {
  clearCanvasNavigationContext(workspaceId)
  return { route: contentProjectsListRoute(workspaceId) }
}
